// ========================================
// CONFIGURAÇÃO DA API - COM SEGURANÇA
// src/config/api.js
// ========================================

import { encontrarConteudoBloqueado } from '../utils/conteudoBloqueado';

// Para desenvolvimento local, use: 'http://localhost:3001/api'
// Para produção, use: 'http://62.169.23.162/api'
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// ========================================
// FUNÇÕES AUXILIARES
// ========================================

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

const getCookie = (name) => {
    if (typeof document === 'undefined') return null;

    const cookies = document.cookie ? document.cookie.split('; ') : [];
    for (const cookie of cookies) {
        const eqIndex = cookie.indexOf('=');
        const cookieName = eqIndex >= 0 ? cookie.slice(0, eqIndex) : cookie;
        if (cookieName === name) {
            const cookieValue = eqIndex >= 0 ? cookie.slice(eqIndex + 1) : '';
            try {
                return decodeURIComponent(cookieValue);
            } catch {
                return cookieValue;
            }
        }
    }
    return null;
};

const getCsrfToken = () => getCookie('csrf_token');

// Função para criar headers (cookies HttpOnly vão via credentials)
const getHeaders = (method = 'GET') => {
    const headers = {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest', // Proteção CSRF básica
    };

    if (!SAFE_METHODS.has(method)) {
        const csrfToken = getCsrfToken();
        if (csrfToken) {
            headers['X-CSRF-Token'] = csrfToken;
        }
    }
    
    return headers;
};

// ========================================
// VALIDAÇÃO ANTES DE ENVIAR
//
// Antes, qualquer suspeita virava "Dados inválidos detectados" — o usuário não
// sabia qual campo ajustar. Agora o erro carrega campo, motivo e trecho, e as
// telas mostram um alerta explicando o que corrigir para conseguir salvar.
// ========================================
const validateRequestData = (data) => {
    if (!data) return true;

    const bloqueio = encontrarConteudoBloqueado(data);

    if (bloqueio) {
        const erro = new Error(bloqueio.message);
        erro.codigo = 'CONTEUDO_BLOQUEADO';
        erro.campo = bloqueio.campo;
        erro.motivo = bloqueio.motivo;
        erro.comoCorrigir = bloqueio.comoCorrigir;
        erro.trecho = bloqueio.trecho;
        throw erro;
    }

    return true;
};

// Função para tratar erros da API
const handleResponse = async (response) => {
    let data;
    
    try {
        // Algumas rotas podem retornar 204/sem body
        if (response.status === 204) {
            data = {};
        } else {
            data = await response.json();
        }
    } catch (e) {
        data = {};
    }
    
    if (!response.ok) {
        // Rate limit excedido
        if (response.status === 429) {
            throw new Error(data.message || 'Muitas requisições. Aguarde um momento.');
        }
        
        // Bloqueado por segurança
        if (response.status === 403) {
            const erro = new Error(data.message || 'Acesso negado');

            // O backend manda campo/motivo/trecho quando recusa um texto.
            // Repassar isso deixa a tela mostrar "ajuste a Observação do Pedido"
            // em vez de um erro genérico de salvamento.
            if (data.codigo) {
                erro.codigo = data.codigo;
                erro.campo = data.campo;
                erro.motivo = data.motivo;
                erro.comoCorrigir = data.comoCorrigir;
                erro.trecho = data.trecho;
            }

            throw erro;
        }
        
        throw new Error(data.message || 'Erro na requisição');
    }
    
    return data;
};

// ========================================
// RECUPERAÇÃO AUTOMÁTICA DE CSRF
// Quando o backend retorna CSRF_MISSING ou CSRF_INVALID,
// busca um novo token e repete a requisição.
// ========================================
let isFetchingCsrf = false;
let csrfPromise = null;

const refreshCsrfToken = async () => {
    // Se já está buscando, espera o resultado
    if (isFetchingCsrf && csrfPromise) {
        return csrfPromise;
    }

    isFetchingCsrf = true;
    csrfPromise = (async () => {
        try {
            const response = await fetch(`${API_URL}/auth/csrf-token`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                credentials: 'include',
            });

            if (!response.ok) {
                console.warn('⚠️ Falha ao buscar CSRF token:', response.status);
                return false;
            }

            // Aguardar um pequeno delay para o browser processar o Set-Cookie
            await new Promise(resolve => setTimeout(resolve, 100));

            const newToken = getCsrfToken();
            return !!newToken;
        } catch (error) {
            console.error('❌ Erro ao buscar CSRF token:', error);
            return false;
        } finally {
            isFetchingCsrf = false;
            csrfPromise = null;
        }
    })();

    return csrfPromise;
};

// ========================================
// MUTEX PARA REFRESH - EVITAR MÚLTIPLOS REFRESHES SIMULTÂNEOS
// ========================================
let isRefreshing = false;
let refreshPromise = null;

const refreshSession = async () => {
    // Se já está fazendo refresh, espera o resultado
    if (isRefreshing && refreshPromise) {
        return refreshPromise;
    }

    isRefreshing = true;
    refreshPromise = (async () => {
        try {
            const response = await fetch(`${API_URL}/auth/refresh`, {
                method: 'POST',
                headers: getHeaders('POST'),
                credentials: 'include',
            });

            if (!response.ok) return false;

            // Mesmo que a resposta não tenha JSON (não deve acontecer), ok
            return true;
        } catch {
            return false;
        } finally {
            isRefreshing = false;
            refreshPromise = null;
        }
    })();

    return refreshPromise;
};

const request = async ({ method, endpoint, body, includeAuth = true, allowRefresh = true, allowCsrfRetry = true }) => {
    // Valida dados antes de enviar (verifica XSS, mas NÃO faz escape HTML)
    // O escape HTML deve ser feito apenas na EXIBIÇÃO, não no envio para o banco
    const hasBody = body !== undefined && body !== null;
    if (hasBody) validateRequestData(body);

    // Para métodos que precisam de CSRF, garantir que o token existe antes de enviar
    if (!SAFE_METHODS.has(method) && !getCsrfToken() && allowCsrfRetry) {
        await refreshCsrfToken();
    }

    const response = await fetch(`${API_URL}${endpoint}`, {
        method,
        headers: getHeaders(method),
        credentials: 'include',
        body: hasBody ? JSON.stringify(body) : undefined,
    });

    // Recuperação automática de CSRF: se receber erro CSRF, busca novo token e repete
    if (response.status === 403 && allowCsrfRetry) {
        try {
            const clonedResponse = response.clone();
            const errorData = await clonedResponse.json();
            if (errorData.code === 'CSRF_MISSING' || errorData.code === 'CSRF_INVALID') {
                const csrfOk = await refreshCsrfToken();
                if (csrfOk) {
                    return request({ method, endpoint, body, includeAuth, allowRefresh, allowCsrfRetry: false });
                }
            }
        } catch {
            // Se não conseguiu ler o body, prosseguir normalmente
        }
    }

    if (
        response.status === 401 &&
        includeAuth &&
        allowRefresh &&
        endpoint !== '/auth/login' &&
        endpoint !== '/auth/refresh'
    ) {
        const ok = await refreshSession();
        if (ok) {
            return request({ method, endpoint, body, includeAuth, allowRefresh: false });
        }
    }

    return handleResponse(response);
};

export { refreshSession, refreshCsrfToken };

// ==================== MÉTODOS HTTP SEGUROS ====================

export const api = {
    get: async (endpoint, includeAuth = true) => {
        try {
            return await request({ method: 'GET', endpoint, includeAuth });
        } catch (error) {
            console.error(`Erro GET ${endpoint}:`, error);
            throw error;
        }
    },

    post: async (endpoint, body, includeAuth = true) => {
        try {
            return await request({ method: 'POST', endpoint, body, includeAuth });
        } catch (error) {
            console.error(`Erro POST ${endpoint}:`, error);
            throw error;
        }
    },

    put: async (endpoint, body, includeAuth = true) => {
        try {
            return await request({ method: 'PUT', endpoint, body, includeAuth });
        } catch (error) {
            console.error(`Erro PUT ${endpoint}:`, error);
            throw error;
        }
    },

    patch: async (endpoint, body, includeAuth = true) => {
        try {
            return await request({ method: 'PATCH', endpoint, body, includeAuth });
        } catch (error) {
            console.error(`Erro PATCH ${endpoint}:`, error);
            throw error;
        }
    },

    delete: async (endpoint, includeAuth = true) => {
        try {
            return await request({ method: 'DELETE', endpoint, includeAuth });
        } catch (error) {
            console.error(`Erro DELETE ${endpoint}:`, error);
            throw error;
        }
    }
};

export default api;
